import { useScenariosStore } from '@/stores/scenarios';
import { useWorkpackagesStore } from '@/stores/workpackages';
import contextEnum from '@enums/note-contexts';
import to from 'await-to-js';
import axios from 'axios';
import { camelCase, cloneDeep, get } from 'lodash';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useNotesStore = defineStore('notes', () => {
  // State
  const notes = ref({});

  // Getters
  const getScenarioNotes = computed(() => ({ scenarioId }) => {
    return notes.value[`${contextEnum.scenario}/${scenarioId}`];
  });
  const getNotes = computed(() => ({ id, context }) => {
    return notes.value[`${context}/${id}`];
  });

  // Private helpers (previous mutations)
  function setNotes({ id, context, notes: _notes }) {
    const notesClone = { ...notes.value };
    notesClone[`${context}/${id}`] = _notes;
    notes.value = notesClone;
  }
  function addNote({ id, context, note }) {
    const localNotes = notes.value;
    localNotes[`${context}/${id}`] = [note, ...localNotes[`${context}/${id}`]];
    notes.value = { ...localNotes };
  }

  function updateNoteHelper({ id, context, update, noteId }) {
    const notesClone = cloneDeep(notes.value);
    const updateNoteIndex = notesClone[`${context}/${id}`].findIndex(
      currentNote => noteId === currentNote._id
    );
    // No note found to update
    if (updateNoteIndex === -1) return;
    // update note
    notesClone[`${context}/${id}`][updateNoteIndex] = {
      ...notesClone[`${context}/${id}`][updateNoteIndex],
      ...update,
    };
    notes.value = notesClone;
  }

  function deleteNoteHelper({ id, context, noteId }) {
    const localNotes = notes.value;
    localNotes[`${context}/${id}`] = localNotes[`${context}/${id}`].filter(
      currentNote => currentNote._id !== noteId
    );
    notes.value = { ...localNotes };
  }

  // Actions
  async function fetchNotes({ params }) {
    const { id, context } = params;

    const [err, res] = await to(axios.get(`/api/notes/${context}/${id}`));
    if (err) throw new Error(err.message);

    // Sort by date
    const newNotes = get(res, 'data', []);
    setNotes({ id, context, notes: newNotes });
  }

  async function fetchCannGroupNotes({ params }) {
    const { id, scenarioId, context } = params;

    const [err, res] = await to(axios.get(`/api/notes/${context}/${scenarioId}/${id}`));
    if (err) throw new Error(err.message);

    const newNotes = get(res, 'data', []);

    setNotes({ id, context, notes: newNotes });
  }

  async function postNote({ params, options = {} }) {
    const scenariosStore = useScenariosStore();
    const workpackagesStore = useWorkpackagesStore();
    const { message, id, context, workpackageId, scenarioId } = params;
    const { storeToUpdate = context, mutationName = 'updateNoteCountMutation' } = options;

    const body = { message };

    if (workpackageId) body.workpackageId = workpackageId;
    if (scenarioId) body.scenarioId = scenarioId;

    const [err, res] = await to(axios.post(`/api/notes/${context}/${id}`, body));
    const note = get(res, 'data');

    if (err) throw new Error(err.message);
    addNote({ id, context, note });

    let storeToUse;
    if (storeToUpdate === 'workpackage') {
      storeToUse = workpackagesStore;
    }

    if (storeToUpdate === 'scenario') {
      storeToUse = scenariosStore;
    }

    if (storeToUse) {
      storeToUse[mutationName]({ [`${camelCase(context)}Id`]: id });
    }
  }

  async function updateNote({ params }) {
    const { message, context, id, noteId } = params;
    const body = { message };
    const [err] = await to(axios.patch(`/api/notes/${noteId}`, body));
    if (err) throw new Error(err.message);
    updateNoteHelper({ id, context, update: body, noteId });
  }

  async function deleteNote({ params, options = {} }) {
    const scenariosStore = useScenariosStore();
    const workpackagesStore = useWorkpackagesStore();
    const { context, noteId, id } = params;
    const { storeToUpdate = context, mutationName = 'updateNoteCountMutation' } = options;

    const [err] = await to(axios.delete(`/api/notes/${noteId}`));
    if (err) throw new Error(err.message);
    deleteNoteHelper({ id, context, noteId });

    let storeToUse;
    if (storeToUpdate === 'workpackage') {
      storeToUse = workpackagesStore;
    }

    if (storeToUpdate === 'scenario') {
      storeToUse = scenariosStore;
    }

    storeToUse[mutationName]({ [`${camelCase(context)}Id`]: id, difference: -1 });
  }

  return {
    // State
    notes,
    // Getters
    getScenarioNotes,
    getNotes,
    // Actions
    fetchNotes,
    fetchCannGroupNotes,
    postNote,
    updateNote,
    deleteNote,
  };
});
